/**
 * INT-0027 — location picker with create-on-type. Type-ahead over the org's
 * existing locations; an unmatched name offers "Create — <name>". Matching is
 * CASE-INSENSITIVE with whitespace collapsed (spec: a typo'd duplicate
 * pollutes grid columns, the public schedule, and ICS feeds forever). The
 * first-typed casing is what gets stored. Location is OPTIONAL — a "No
 * location" row clears the selection.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, MapPin, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ComboLocation {
  _id: string;
  name?: string;
  parentLocation?: unknown;
  /** Pre-computed display label (e.g. "Venue - Court 1"). */
  displayName: string;
}

const normalize = (v: string) => v.trim().replace(/\s+/g, ' ');
const foldedKey = (v: string) => normalize(v).toLowerCase();

export default function LocationCombobox({
  locations,
  value,
  onSelect,
  onCreate,
  disabled,
}: {
  locations: ComboLocation[];
  /** Selected location _id, or '' for none. */
  value: string;
  onSelect: (id: string) => void;
  /** Create a name-only location; resolves to the created doc (with _id). */
  onCreate: (name: string) => Promise<{ _id: string } | null>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const selected = locations.find((l) => l._id === value) || null;
  const cleanQuery = normalize(query);
  // Exact case-insensitive match anywhere in the list → never offer Create
  // for it; picking the existing one is the only path (dedupe by design).
  const exactMatch = useMemo(
    () => locations.find((l) => foldedKey(l.displayName) === foldedKey(cleanQuery) || foldedKey(l.name || '') === foldedKey(cleanQuery)),
    [locations, cleanQuery],
  );

  const create = async () => {
    if (!cleanQuery || creating) return;
    setCreating(true);
    try {
      const doc = await onCreate(cleanQuery);
      if (doc && doc._id) {
        onSelect(doc._id);
        setOpen(false);
        setQuery('');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid="location-combobox"
        >
          <span className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{selected ? selected.displayName : 'No location'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type a new location…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {cleanQuery ? 'No matching location.' : 'No locations yet. Type a name to create one.'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onSelect(''); setOpen(false); setQuery(''); }}
              >
                <X className="mr-2 h-4 w-4 text-muted-foreground" />
                No location
                {!value && <Check className="ml-auto h-4 w-4" />}
              </CommandItem>
              {locations.map((l) => (
                <CommandItem
                  key={l._id}
                  value={l.displayName}
                  onSelect={() => { onSelect(l._id); setOpen(false); setQuery(''); }}
                >
                  <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{l.displayName}</span>
                  {value === l._id && <Check className="ml-auto h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {cleanQuery && !exactMatch && (
              <CommandGroup forceMount>
                <CommandItem value={`__create__${cleanQuery}`} onSelect={create} disabled={creating} forceMount>
                  <Plus className="mr-2 h-4 w-4" />
                  {creating ? 'Creating…' : <>Create&nbsp;<span className="font-semibold">&ldquo;{cleanQuery}&rdquo;</span></>}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
